import type { UserPriceRequest, UserSettingsRequest } from '@domains/billing-settings/priv/CustomerUserSettings/api/api'
import { CustomerUserSettings } from '@domains/billing-settings/priv/CustomerUserSettings/usecases/CustomerUserSettings'
import { createCustomTheme } from '@domains/subscription/theme'
import { ThemeProvider } from '@mui/material'
import useMaterialNotification from 'hooks/useMaterialNotification'
import {
  useDeleteUserPrice,
  useDeleteUserSettings,
  useUpdateUserPrice,
  useUpdateUserSettings,
  useUserLimit,
  useUserPrice,
} from '../api/queries'

interface CustomerUserSettingsFactoryProps {
  customerId: string
  onChange?: () => void
}

export const CustomerUserSettingsFactory = ({ customerId, onChange }: CustomerUserSettingsFactoryProps) => {
  const { data: userSettings, isLoading: isUserSettingsLoading } = useUserLimit(customerId)
  const { data: userPrice, isLoading: isUserPriceLoading } = useUserPrice(customerId)

  const { mutate: updateUserSettings, isLoading: isUpdatingUserSettings } = useUpdateUserSettings(customerId)
  const { mutate: updateUserPrice, isLoading: isUpdatingUserPrice } = useUpdateUserPrice(customerId)
  const { mutate: deleteUserPrice, isLoading: isDeletingUserPrice } = useDeleteUserPrice(customerId)
  const { mutate: deleteUserSettings, isLoading: isDeletingUserSettings } = useDeleteUserSettings(customerId)

  const isLoading = isUserSettingsLoading || isUserPriceLoading
  const isPatching = isUpdatingUserSettings || isUpdatingUserPrice
  const isDeleting = isDeletingUserPrice || isDeletingUserSettings

  const notify = useMaterialNotification()

  const handlePriceChange = (price: UserPriceRequest) => {
    updateUserPrice(price, {
      onSuccess: () => {
        notify('Updated user price')
        onChange?.()
      },
    })
  }

  const handleSettingsChange = (settings: UserSettingsRequest) => {
    updateUserSettings(settings, {
      onSuccess: () => {
        notify('Updated user settings')
        onChange?.()
      },
    })
  }

  const handleDeletePrice = () => {
    deleteUserPrice(undefined, {
      onSuccess: () => {
        notify('Deleted user price')
        onChange?.()
      },
    })
  }

  const handleDeleteSettings = () => {
    deleteUserSettings(undefined, {
      onSuccess: () => {
        notify('Deleted user settings')
        onChange?.()
      },
    })
  }

  return (
    <ThemeProvider theme={createCustomTheme()}>
      <CustomerUserSettings
        isLoading={isLoading}
        isPatching={isPatching}
        onPriceChange={handlePriceChange}
        onSettingsChange={handleSettingsChange}
        onPriceDelete={handleDeletePrice}
        onSettingsDelete={handleDeleteSettings}
        price={userPrice}
        settings={userSettings}
        isDeleting={isDeleting}
      />
    </ThemeProvider>
  )
}
